import { inject, Injectable, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { environment } from "../../../environments/env";
import { CookieConsentService } from "./cookie-consent.service";

@Injectable({
  providedIn: "root",
})
export class PixelInitializationService {
  private tiktokLoaded = false;
  private metaLoaded = false;
  private platformId = inject(PLATFORM_ID);
  private _consent = inject(CookieConsentService);

  constructor() {}

  /**
   * Initializes TikTok and Meta pixels according to the current environment
   * config and the user's advertising consent.
   *
   * Hotjar/performance gating is handled by AppComponent effect.
   * Withdrawal page-reload semantics: already-loaded scripts unload on the
   * next full page load only — they cannot be torn down in-session.
   *
   * Only runs in the browser (SSR guard). Idempotency guards on
   * tiktokLoaded/metaLoaded make this safe to call from a reactive effect.
   */
  initializePixels(): void {
    // SSR guard — never run on the server
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Advertising consent gates both TikTok and Meta pixels
    if (this._consent.advertising()) {
      this.initializeTikTokPixel();
      this.initializeMetaPixel();
    }
  }

  /**
   * Inicializa el pixel de TikTok con el ID del ambiente actual
   */
  private initializeTikTokPixel(): void {
    if (this.tiktokLoaded) {
      console.warn("TikTok Pixel ya está cargado");
      return;
    }

    if (!environment.tiktok.enabled) {
      console.log("TikTok Pixel está deshabilitado en el ambiente actual");
      return;
    }

    const pixelId = environment.tiktok.pixelId;
    console.log(`Inicializando TikTok Pixel: ${pixelId}`);

    // Script de inicialización de TikTok
    (function (w: any, d: Document, t: string) {
      w.TiktokAnalyticsObject = t;
      const ttq = (w[t] = w[t] || []);
      ttq.methods = [
        "page",
        "track",
        "identify",
        "instances",
        "debug",
        "on",
        "off",
        "once",
        "ready",
        "alias",
        "group",
        "enableCookie",
        "disableCookie",
        "holdConsent",
        "revokeConsent",
        "grantConsent",
      ];
      ttq.setAndDefer = function (t: any, e: string) {
        t[e] = function (...args: unknown[]) {
          t.push([e, ...args]);
        };
      };
      for (const method of ttq.methods) ttq.setAndDefer(ttq, method);
      ttq.instance = function (t: string) {
        const e = ttq._i[t] || [];
        for (const method of ttq.methods) ttq.setAndDefer(e, method);
        return e;
      };
      ttq.load = function (e: string, n?: any) {
        const r = "https://analytics.tiktok.com/i18n/pixel/events.js";
        ttq._i = ttq._i || {};
        ttq._i[e] = [];
        ttq._i[e]._u = r;
        ttq._t = ttq._t || {};
        ttq._t[e] = +new Date();
        ttq._o = ttq._o || {};
        ttq._o[e] = n || {};
        const o = document.createElement("script");
        o.type = "text/javascript";
        o.async = true;
        o.src = r + "?sdkid=" + e + "&lib=" + t;
        const a = document.getElementsByTagName("script")[0];
        a.parentNode!.insertBefore(o, a);
      };

      ttq.load(pixelId);
      ttq.page();
    })(window, document, "ttq");

    this.tiktokLoaded = true;
  }

  /**
   * Inicializa el pixel de Meta (Facebook) con el ID del ambiente actual
   */
  private initializeMetaPixel(): void {
    if (this.metaLoaded) {
      console.warn("Meta Pixel ya está cargado");
      return;
    }

    if (!environment.meta.enabled) {
      console.log("Meta Pixel está deshabilitado en el ambiente actual");
      return;
    }

    const pixelId = environment.meta.pixelId;
    console.log(`Inicializando Meta Pixel: ${pixelId}`);

    // Script de inicialización de Meta
    (function (f: any, b: Document, e: string, v: string) {
      if (f.fbq) return;
      const n: any = (f.fbq = function (...args: unknown[]) {
        if (n.callMethod) {
          n.callMethod(...args);
        } else {
          n.queue.push(args);
        }
      });
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      const t = b.createElement(e) as HTMLScriptElement;
      t.async = true;
      t.src = v;
      const s = b.getElementsByTagName(e)[0];
      s.parentNode!.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js",
    );

    (window as any).fbq("init", pixelId);

    // Agregar noscript para Meta Pixel
    this.addMetaPixelNoscript(pixelId);

    this.metaLoaded = true;
  }

  /**
   * Agrega el elemento noscript de Meta Pixel al body
   */
  private addMetaPixelNoscript(pixelId: string): void {
    const noscript = document.createElement("noscript");
    const img = document.createElement("img");
    img.height = 1;
    img.width = 1;
    img.style.display = "none";
    img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);
  }

  /**
   * Verifica si los pixels están cargados
   */
  arePixelsLoaded(): { tiktok: boolean; meta: boolean } {
    return {
      tiktok: this.tiktokLoaded,
      meta: this.metaLoaded,
    };
  }
}
